// Parent rotates a kid's password. Revokes the kid's active sessions on the
// server (handled by the API), so any handed-off device must sign in again.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Dialog, DialogContent, Field, Input } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError } from '~/lib/api';
import { resetKidPassword, type Kid } from '~/lib/family';

export function ResetPasswordModal({
  open,
  onOpenChange,
  kid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kid: Kid | null;
}) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!kid) return;
      await resetKidPassword(kid.id, password);
    },
    onSuccess: () => onOpenChange(false),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('app.common.error_generic')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('app.parent.reset_password')}
        description={kid?.displayName ?? kid?.username ?? undefined}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label={t('app.parent.field_password')} hint={t('app.parent.field_password_hint')}>
            {(id) => (
              <Input
                id={id}
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          {error && <Alert tone="danger">{error}</Alert>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('app.common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {t('app.common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
